<script setup>
import { useApi } from '@/composables/useApi'
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import HorizontalStackBar from '@/@core/components/graph/HorizontalStackBar.vue'
import HorizontalBar from '@/@core/components/graph/HorizontalBar.vue'
import VerticalBar from '@/@core/components/graph/VerticalBar.vue'
import { VSkeletonLoader } from 'vuetify/lib/labs/components.mjs'

definePage({
  meta: {
    name: "Survey Setup",
  },
})

const route = useRoute()
const router = useRouter()

const surveyType = computed(() => route.params.type)
const surveyId = computed(() => route.params.survey ?? null)

const survey = ref()

// Validate Survey Type
if (!surveyType) {
  router.push({
    name: 'invalid-url',
    params: { code: 404 },
  })
}

if (surveyId.value) {
  await useApi(createUrl(`/survey/surveys/${surveyId.value}`), {
    beforeFetch: () => {},
    afterFetch: ({ data }) => {
      survey.value = data
    },
    onFetchError: error => {
      router.push({
        name: 'invalid-url',
        params: { code: 404 },
      })
    },
  })
}

useApi(createUrl(`/survey/surveys/${surveyId.value}/analytics`), {
  beforeFetch: () => {},
  afterFetch: ({ data }) => {
    survey.value = {
      ...survey.value,
      ...data,
    }
  },
})

const questions = ref([])

// Load Form
const {
  isFetching: isLoading,
} = useApi(createUrl(`/survey/form-steps`, {
  query: {
    search: { 
      survey_id: surveyId.value,
    },
    with: ['questionGroups'],  
  }, 
}), {
  afterFetch: ({ data }) => {
    data.forEach(step => {
      step.question_groups.forEach(group => {
        const filterQuestions = group.questions.filter(item => ['select', 'radio', 'radio_rating', 'star_rating', 'smile', 'scale'].includes(item.question_type))

        filterQuestions.forEach(question => {
          // Load Question Response
          useApi(createUrl(`/survey/questions/${question.id}`), {
            afterFetch: ({ data }) => {
              const grouped = data.answers.reduce((acc, item) => {
                const option = question.question_options.find(x => x.id === item.question_option_id)
    
                if (!acc[option.id]) {
                  acc[option.id] = {
                    question_option_id: option.id,
                    label: option.label,
                    value: option.value,
                    count: 0,
                    responses: [],
                    color: option.color,
                  }
                }
    
                acc[option.id].count += 1
                acc[option.id].responses.push(item)
    
                return acc
              }, {})

              const totalAnswers = data.answers.length

              let graphData = Object.values(grouped).map(group => ({
                question_option_id: group.question_option_id,
                label: group.label,
                value: group.value,
                count: group.count,
                percentage: ((group.count / totalAnswers) * 100).toFixed(2),
                color: group.color,
              }))

              // For Scale Type Questions, include all options even if count is 0
              if (question.question_type === 'scale') {
                const scaleColors = colorPaletteForNPS()

                graphData = question.question_options.map((item, optionIndex) => {
                  let data = graphData.find(x => x.question_option_id === item.id)

                  return {
                    question_option_id: item.id,
                    label: item.label,
                    value: item.value,
                    count: data?.count ?? 0,
                    percentage: data?.percentage ?? 0,
                    color: item.color ?? scaleColors[optionIndex],
                  }
                })
              }

              if (totalAnswers > 0) {
                questions.value.push({
                  ...data,
                  total_answers: totalAnswers,
                  graph_data: graphData,
                })
              }
            },
          })
        })
      })
    })
  },
})

const calculateColumn = length => {
  if (length <= 2) {
    return 6
  }

  return 12
}

// const selectedSchedule = ref()
</script>

<template>
  <VRow v-if="isLoading">
    <VSkeletonLoader
      class="mx-auto"
      type="card"
      width="100%"
    />
  </VRow>
  <VRow v-else-if="questions.length > 0">
    <VCol col="12">
      <div class="d-flex align-center">
        <div>
          <h4 class="text-h3 font-weight-bold text-primary text-capitalize">
            {{ survey.title }}
          </h4>
        </div>
        <VSpacer />
        <!--
          <div style="width: 200px;">
          <AppSelect
          v-model="selectedSchedule"
          :items="survey.schedules ?? []"
          :item-title="(item) => item.name"
          :item-value="(item) => item.id"
          placeholder="Filter By Schedule"
          :return-object="false"
          />
          </div> 
        -->
        <VBtn append-icon="tabler-external-link">
          <RouterLink
            class="text-white"
            :to="{ name: 'app-survey-collection-customer', params: { id: surveyId } }"
          >
            View Survey Responses
          </RouterLink>
        </VBtn>
      </div>
    </VCol>

    <VCol cols="12">
      <VRow>
        <!-- Total Scheduled Surveys Card -->
        <VCol
          cols="12"
          sm="4"
        >
          <VCard
            elevation="2"
            class="pa-4"
          >
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h4 font-weight-bold primary--text">
                  {{ survey.number_of_scheduled_surveys }}
                </div>
                <div class="text-subtitle-1 text-medium-emphasis mt-1">
                  Scheduled Surveys
                </div>
              </div>
              <VAvatar
                color="primary"
                size="56"
                class="elevation-1"
              >
                <VIcon
                  icon="tabler-clipboard-text"
                  size="32"
                />
              </VAvatar>
            </div>
          </VCard>
        </VCol>

        <!-- Collected Surveys Card -->
        <VCol
          cols="12"
          sm="4"
        >
          <VCard
            elevation="2"
            class="pa-4"
          >
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h4 font-weight-bold success--text">
                  {{ survey.number_of_collected_surveys }}
                </div>
                <div class="text-subtitle-1 text-medium-emphasis mt-1">
                  Collected Surveys
                </div>
              </div>
              <VAvatar
                color="success"
                size="56"
                class="elevation-1"
              >
                <VIcon
                  icon="tabler-checkup-list"
                  size="32"
                />
              </VAvatar>
            </div>
          </VCard>
        </VCol>

        <!-- Not Collected Surveys Card -->
        <VCol
          cols="12"
          sm="4"
        >
          <VCard
            elevation="2"
            class="pa-4"
          >
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h4 font-weight-bold warning--text">
                  {{ survey.number_of_not_collected_surveys }}
                </div>
                <div class="text-subtitle-1 text-medium-emphasis mt-1">
                  Not Collected Surveys
                </div>
              </div>
              <VAvatar
                color="warning"
                size="56"
                class="elevation-1"
              >
                <VIcon
                  icon="tabler-clipboard-off"
                  size="32"
                />
              </VAvatar>
            </div>
          </VCard>
        </VCol>
      </VRow>
    </VCol>

    <template
      v-for="(question, questionIndex) in questions"
      :key="questionIndex"
    >
      <VCol cols="12">
        <VCard>
          <VCardItem>
            <VCardTitle
              class="text-h6"
              style="white-space: normal;"
            >
              {{ question.label }}
            </VCardTitle>
          </VCardItem>
          <VDivider />
          <VCardText>
            <HorizontalStackBar
              v-if="question.question_options.length <= 3"
              :graph-data="question.graph_data"
            />
            <VerticalBar
              v-else-if="question.question_options.length >= 9 || question.question_type === 'scale' || question.question_type === 'radio_rating' || question.question_type === 'star_rating' || question.question_type === 'smile'"
              :graph-data="question.graph_data"
            />
            <HorizontalBar
              v-else-if="question.question_options.length >= 4 && question.question_options.length <= 8"
              :graph-data="question.graph_data"
            />
          </VCardText>
        </VCard>
      </VCol>
    </template>
  </VRow>
  <VCard v-else>
    <VCardText>
      <span class="d-block text-center">
        No analytics data available for this survey
      </span>
    </VCardText>
  </VCard>

  <!--
    <VCard class="mt-8">
    <VCardItem title="IF The Graph Data Count IS Above OR Equal to 5 ">
    <template #append>
    <MoreBtn />
    </template>
    </VCardItem>

    <VCardText>
    <HorizontalBar :graph-data="testingGraph" />
    </VCardText>
    </VCard>

    <VCard class="mt-8">
    <VCardItem title="IF The Question Type NPS">
    <template #append>
    <VBtn
    variant="tonal"
    append-icon="tabler-chevron-down"
    >
    January
    </VBtn>
    </template>
    </VCardItem>

    <VCardText>
    <VerticalBar :graph-data="testingGraph" />
    </VCardText>
    </VCard> 
  -->
</template>
